import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resolveSiteContent, type ResolvedContent } from '@ircf/config';
import { publicApi } from '../lib/api/endpoints.js';
import { queryKeys } from '../lib/api/queryKeys.js';

/**
 * Published site content.
 *
 * The provider never blocks rendering. `resolveSiteContent(null)` returns the
 * full configuration defaults synchronously, so the first paint has real
 * headlines and copy; when the CMS response arrives it replaces any slot the
 * administrator has actually published. A failed content fetch therefore
 * degrades to the placeholder site rather than to an error page.
 */

const ContentCtx = createContext<ResolvedContent | null>(null);

export function ContentProvider({ children }: { children: ReactNode }): JSX.Element {
  const { data } = useQuery({
    queryKey: queryKeys.publicSite.content,
    queryFn: publicApi.site,
    staleTime: 5 * 60_000,
    // Content is presentational; a retry storm on an unavailable API is worse
    // than showing configured defaults.
    retry: 1,
  });

  const value = useMemo(() => resolveSiteContent(data ?? null), [data]);

  return <ContentCtx.Provider value={value}>{children}</ContentCtx.Provider>;
}

export function useContent(): ResolvedContent {
  const context = useContext(ContentCtx);
  if (!context) {
    throw new Error('useContent must be used within <ContentProvider>');
  }
  return context;
}
